export const GLOSSARY = [
    // ---- ITIL / industry-standard --------------------------------------
    {
        term: 'SLA',
        category: 'itil',
        definition: 'Service Level Agreement — the contractual response and resolution targets ' +
            'you commit to for each ticket priority. StyxDesk computes the due times at ' +
            'create time and watches for breaches.',
        related: ['MTTR', 'Business Hours', 'Priority'],
    },
    {
        term: 'MTTR',
        category: 'itil',
        definition: 'Mean Time To Resolve — average time from ticket creation to Resolved status. ' +
            'A core KPI on every ITSM dashboard; lower is better. Shown per engineer and ' +
            'per client on the Reports page.',
        related: ['SLA', 'KPI'],
    },
    {
        term: 'CSAT',
        category: 'itil',
        definition: 'Customer Satisfaction Score — typically a 1–5 rating sent to the requester ' +
            'after a ticket is Resolved. StyxDesk auto-issues a CSAT survey on closure ' +
            'and rolls the scores up into the Voice of Customer report.',
        related: ['NPS', 'CES'],
    },
    {
        term: 'NPS',
        category: 'itil',
        definition: 'Net Promoter Score — 0–10 rating answering "How likely are you to recommend ' +
            'us?". 9–10 = Promoter, 7–8 = Passive, 0–6 = Detractor. Run quarterly as a ' +
            'relationship survey, not per-ticket.',
        related: ['CSAT', 'CES'],
    },
    {
        term: 'CES',
        category: 'itil',
        definition: 'Customer Effort Score — 1–7 rating answering "How much effort did you have ' +
            'to put in to resolve this?" Sent per-ticket alongside CSAT. Low effort ' +
            'correlates with retention more strongly than high satisfaction does.',
        related: ['CSAT', 'NPS'],
    },
    {
        term: 'Incident',
        category: 'itil',
        definition: 'An unplanned interruption to a service OR a reduction in service quality. ' +
            'Examples: VPN is down, printer is jammed, email is slow. Distinct from a ' +
            'Service Request, which is a planned ask for something new (e.g. "set up a ' +
            'new user account").',
        related: ['Service Request', 'Priority', 'Impact'],
    },
    {
        term: 'Service Request',
        category: 'itil',
        definition: 'A planned request for a standard service — onboarding a new user, ' +
            'installing software, requesting access. Distinct from an Incident (which ' +
            'fixes a broken thing). Often routed through an approval workflow.',
        related: ['Incident'],
    },
    {
        term: 'Business Hours',
        category: 'itil',
        definition: 'The hours during which SLA timers run. Defined per SLA Policy with a ' +
            'weekly schedule and a holiday calendar. Example: 9–18 Mon–Fri excluding ' +
            'India public holidays. A ticket created at 17:55 Friday with a 4-hour ' +
            'resolution SLA is due 10:55 Monday, not 21:55 Friday.',
        related: ['SLA', 'Holiday Calendar'],
    },
    // ---- StyxDesk-specific --------------------------------------------
    {
        term: 'Watcher',
        category: 'styxdesk',
        definition: 'An internal user who has chosen to follow a ticket. Watchers receive ' +
            'all system emails (created, status change) plus in-app notifications. ' +
            'Different from the assigned engineer (who does the work) or the requester ' +
            '(who raised it).',
        related: ['CC Emails', 'Requester', 'Assignee'],
    },
    {
        term: 'CC Emails',
        category: 'styxdesk',
        definition: 'External email addresses copied on system notifications for a ticket. ' +
            'Use this for client managers, partner PMs, or vendor coordinators who ' +
            'need updates but don\'t have a user account. Internal users should be ' +
            'added as Watchers instead.',
        related: ['Watcher'],
    },
    {
        term: 'Service Location',
        category: 'styxdesk',
        definition: 'Free-text "where the work needs to be performed" on a ticket. Type any ' +
            'address — suggestions appear from the client\'s registered sites. Picking ' +
            'a suggestion auto-links the structured Site so the SLA timezone attaches ' +
            'correctly; typing a one-off address is also fine.',
        related: ['Site', 'SLA'],
    },
    {
        term: 'Ticket Channel',
        category: 'styxdesk',
        definition: 'How a ticket reached the service desk. Manual (walk-in / verbal), Phone, ' +
            'Email, Chat, Portal (customer self-service), API (monitoring tools). ' +
            'Captured at create time and reported on so you can see your channel mix.',
        related: ['Incident', 'Service Request'],
    },
    {
        term: 'Work Order',
        category: 'styxdesk',
        definition: 'A schedulable field-service job raised from a ticket (numbered WO-NNNNNN). ' +
            'It carries the assigned engineer, scheduled window, and status. One ticket ' +
            'can spawn several work orders; each is the schedule, while the Visit is the ' +
            'on-site execution.',
        related: ['Dispatch Board', 'Visit', 'Appointment Slot'],
    },
    {
        term: 'Dispatch Board',
        category: 'styxdesk',
        definition: 'The scheduling view that shows engineers as columns for a date window, ' +
            'their work orders as cards, plus an Unassigned backlog. Click a card to ' +
            'assign an engineer + time. Engineer time-off is surfaced so you don\'t ' +
            'double-book.',
        related: ['Work Order', 'Appointment Slot'],
    },
    {
        term: 'Appointment Slot',
        category: 'styxdesk',
        definition: 'A bookable visit window with a capacity, published by the service desk. ' +
            'Customers self-book a slot for their ticket from the portal; booking ' +
            'schedules a work order and reserves the slot (a full slot can\'t be ' +
            'over-booked).',
        related: ['Work Order', 'Dispatch Board'],
    },
    {
        term: 'Timesheet',
        category: 'styxdesk',
        definition: 'A weekly bundle of an engineer\'s time entries, submitted as a unit for ' +
            'manager approval. Statuses: Draft → Submitted → Approved (or Rejected, ' +
            'which sends entries back to draft to amend). Approved time feeds Phase-3 ' +
            'billing.',
        related: ['Time Entry'],
    },
    {
        term: 'Time Entry',
        category: 'styxdesk',
        definition: 'A single logged block of work — a category (on-site / travel / remote / ' +
            'after-hours), a duration, a start time, and optional links to a ticket or ' +
            'work order. Entries roll up by ISO week into a Timesheet.',
        related: ['Timesheet', 'Work Order'],
    },
    // ---- Monitoring / AIOps (added 2026-06-23) --------------------------
    {
        term: 'AIOps',
        category: 'general',
        definition: 'AI/automation for IT operations. In StyxDesk it means the Monitoring page: ' +
            'a flood of alerts from your monitoring tools is automatically grouped into a ' +
            'single incident, assigned, and closed again when the system recovers — so the ' +
            'team gets one ticket instead of thirty emails. The grouping logic is reliable ' +
            'and runs without any AI key.',
        related: ['Alert Correlation', 'Monitoring Source', 'Noise Reduction', 'Incident'],
    },
    {
        term: 'Monitoring Source',
        category: 'styxdesk',
        definition: 'A configured connection to a monitoring tool (Datadog, Zabbix, Azure Monitor, ' +
            'Prometheus, Grafana, SCOM, or a generic webhook). Each source gives you a secure ' +
            'ingest URL to paste into that tool; alerts sent there flow into StyxDesk and turn ' +
            'into correlated incidents.',
        related: ['AIOps', 'Alert Correlation'],
    },
    {
        term: 'Alert Correlation',
        category: 'general',
        definition: 'Grouping related monitoring alerts (by host + service) into one incident, so a ' +
            'storm of alerts about the same thing produces a single ticket rather than many. ' +
            'A recovery alert closes the incident automatically.',
        related: ['AIOps', 'Alert De-duplication', 'Noise Reduction'],
    },
    {
        term: 'Alert De-duplication',
        category: 'general',
        definition: 'Suppressing repeat alerts about an already-open incident within a time window ' +
            '(the "storm window"). The repeats are counted against the existing incident ' +
            'instead of opening new tickets — the core of noise reduction.',
        related: ['Alert Correlation', 'Noise Reduction'],
    },
    {
        term: 'Noise Reduction',
        category: 'general',
        definition: 'The headline AIOps metric: 1 − (incidents opened ÷ alerts received). If 11 alerts ' +
            'become 3 incidents, that is ~73% noise reduction. Shown on the AI / Ops Value ' +
            'dashboard from your real numbers.',
        related: ['AIOps', 'Alert De-duplication', 'Value Dashboard'],
    },
    {
        term: 'Runbook',
        category: 'itil',
        definition: 'A documented, repeatable fix for a known problem. In StyxDesk a remediation runbook ' +
            'matches an opened monitoring incident and proposes its steps; a human approves, then ' +
            'the steps are posted to the incident. We never auto-run a black-box fix — a person ' +
            'always decides.',
        related: ['Remediation', 'AIOps', 'Knowledge Base'],
    },
    {
        term: 'Remediation',
        category: 'general',
        definition: 'Acting to fix or contain an incident. StyxDesk uses human-gated remediation: a ' +
            'matching runbook proposes a fix, a human approves it, and the safe in-platform step ' +
            '(posting the steps + optional knowledge) runs. Live remote execution is a future, ' +
            'connector-gated capability.',
        related: ['Runbook', 'AIOps'],
    },
    {
        term: 'Value Dashboard',
        category: 'styxdesk',
        definition: 'Insights → AI / Ops Value: measured outcomes drawn only from your own logs — alert ' +
            'noise reduction, MTTR, first-time fix, KB answer rate, asset accuracy, and AI usage ' +
            '+ cost. Empty metrics show "—" rather than a made-up number.',
        related: ['Noise Reduction', 'MTTR', 'First-Time Fix'],
    },
    // ---- Assets / CMDB (added 2026-06-23) -------------------------------
    {
        term: 'CMDB',
        category: 'itil',
        definition: 'Configuration Management Database — the inventory of the IT things you run and ' +
            'service (devices, software, their relationships). StyxDesk ships a "light CMDB": ' +
            'the Assets module plus relationships, discovery, service mapping, and health checks.',
        related: ['Configuration Item', 'Asset'],
    },
    {
        term: 'Configuration Item',
        category: 'itil',
        definition: 'CI — any single item tracked in the CMDB: a laptop, switch, server, access point, ' +
            'POS terminal, or a piece of software. Each ticket and field visit can name the CI(s) ' +
            'it concerns, giving you device service history.',
        related: ['CMDB', 'Asset'],
    },
    {
        term: 'Asset',
        category: 'itil',
        definition: 'A device or CI you own and service, recorded under Admin → Assets: make, serial, ' +
            'warranty, cost, lifecycle, assignee, photos, and links to the tickets/visits that ' +
            'touched it. Warranty/EOL dates drive proactive expiry alerts.',
        related: ['CMDB', 'Configuration Item', 'Lifecycle'],
    },
    {
        term: 'Lifecycle',
        category: 'general',
        definition: 'The state of an asset over its life: in-stock → deployed → in-repair → retired → ' +
            'disposed. "Disposed" is terminal. You can change one asset on its detail, or many at ' +
            'once with the bulk-lifecycle bar on the asset list.',
        related: ['Asset'],
    },
    {
        term: 'MTBF',
        category: 'itil',
        definition: 'Mean Time Between Failures — the average time a device or model runs before it ' +
            'breaks again. StyxDesk computes it per asset/model from linked incidents to surface ' +
            '"top failing assets" worth replacing.',
        related: ['Asset', 'MTTR'],
    },
    {
        term: 'First-Time Fix',
        category: 'itil',
        definition: 'FTF — the share of field tickets resolved in a single on-site visit. A key field-' +
            'service KPI (higher is better); shown on the AI / Ops Value dashboard.',
        related: ['Work Order', 'Value Dashboard', 'MTTR'],
    },
    {
        term: 'Knowledge Base',
        category: 'itil',
        definition: 'KB — your library of how-to articles and SOPs (Standard Operating Procedures). ' +
            'StyxDesk suggests relevant articles on a ticket, lets customers self-serve in the ' +
            'portal, and can turn a resolved ticket into a draft article.',
        related: ['Runbook'],
    },
];
